import { ButtonGroup, Fieldset, Stack, Textarea } from '@chakra-ui/react'
import type { ChangeEventHandler, FC, MouseEventHandler } from 'react'
import { useCallback } from 'react'
import { useEffect, useRef, useState } from 'react'

import { Button } from '@app/components/ui/button'
import { Field } from '@app/components/ui/field'
import { toaster } from '@app/components/ui/toaster'
import useGetObject from '@app/hooks/useGetObject'
import AiDashboardPromptGeneratedReport from '@app/pages/aiDashboard/components/aiDashboardPromptGeneratedReport'
import type { DashboardItem } from '@app/pages/aiDashboard/types'
import { useStore } from '@app/store'
import { useRecurringReportConfigurationPromptTestMutation } from '@graphql/queries'
import { ChatProcessedStateEnum } from '@graphql/types'

interface Props {
  recurringReportConfiguration: DashboardItem
}

const AiDashboardPromptPlayground: FC<Props> = ({ recurringReportConfiguration }) => {
  const [aiPrompt, setAiPrompt] = useState<string>(recurringReportConfiguration.aiPrompt)
  const [chatId, setChatId] = useState<string | null>(null)
  const [isValid, setIsValid] = useState<boolean>(true)
  const [loading, setLoading] = useState<boolean>(false)
  const [hasStartedProcessing, setHasStartedProcessing] = useState<boolean>(false)
  const formRef = useRef<HTMLFormElement>(null)
  const updateObject = useStore.use.updateObject()
  const [, testPrompt] = useRecurringReportConfigurationPromptTestMutation()

  const chat = useGetObject(chatId, 'chat')

  let showSpinner = false
  if (
    loading ||
    (!chat && chatId) ||
    (chat && !hasStartedProcessing) ||
    (chat && [ChatProcessedStateEnum.Processing, ChatProcessedStateEnum.Waiting].includes(chat.processedState))
  ) {
    showSpinner = true
  }

  useEffect(() => {
    if (chat && chat.processedState === ChatProcessedStateEnum.Processing && !hasStartedProcessing) {
      setHasStartedProcessing(true)
    }
  }, [chat, hasStartedProcessing])

  const handleGenerateResults = useCallback<MouseEventHandler<HTMLButtonElement>>(
    async (e) => {
      e.preventDefault()
      const valid = formRef?.current?.checkValidity()
      setIsValid(valid)
      setHasStartedProcessing(false)

      if (valid) {
        setLoading(true)
        setChatId(null)

        try {
          const response = await testPrompt({
            input: { recurringReportConfigurationId: recurringReportConfiguration.id, aiPrompt }
          })
          const chatResponse = response?.data?.recurringReportConfigurationPromptTest?.chat

          setChatId(chatResponse?.id)
        } finally {
          setLoading(false)
        }
      }
    },
    [aiPrompt, recurringReportConfiguration.id, testPrompt]
  )

  const handleSavePrompt = useCallback<MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      e.preventDefault()
      const valid = formRef?.current?.checkValidity()
      setIsValid(valid)

      if (valid) {
        updateObject({ recurringReportConfiguration: { id: recurringReportConfiguration.id, aiPrompt } }).then(() => {
          toaster.create({
            title: 'Updated your report prompt',
            type: 'success'
          })
        })
      }
    },
    [aiPrompt, recurringReportConfiguration.id, updateObject]
  )

  const onPromptChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>((e) => setAiPrompt(e.target.value), [])

  return (
    <Stack gap={6}>
      <Fieldset.Root borderWidth={1} borderStyle="solid" borderColor="border.default" borderRadius="md" px={6} pb={6}>
        <Fieldset.Legend>Edit prompt</Fieldset.Legend>
        <Fieldset.HelperText>
          Write your AI prompt here. Use the "Generate test report" button to test it. Once you are happy with the
          results, save it as the new prompt.
        </Fieldset.HelperText>
        <Fieldset.Content>
          <form ref={formRef}>
            <Stack gap={6}>
              <Field required invalid={!isValid} errorText="An AI prompt is required" disabled={showSpinner}>
                <Textarea
                  value={aiPrompt}
                  onChange={onPromptChange}
                  autoresize
                  minH={64}
                  placeholder="Give me a list of the top and bottom three performing metrics."
                />
              </Field>
              <ButtonGroup justifyContent="flex-end">
                <Button onClick={handleGenerateResults} loading={showSpinner}>
                  Generate test report
                </Button>
                <Button onClick={handleSavePrompt} variant="outline">
                  Save as new prompt
                </Button>
              </ButtonGroup>
            </Stack>
          </form>
        </Fieldset.Content>
      </Fieldset.Root>
      <AiDashboardPromptGeneratedReport
        chat={chat}
        chatId={chatId}
        recurringReportConfiguration={recurringReportConfiguration}
        showSpinner={showSpinner}
      />
    </Stack>
  )
}

export default AiDashboardPromptPlayground
